import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { trackAndSend } from "../lib/metaEvents";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const purchaseFiredRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return setStatus("failed");
    let attempts = 0;
    const maxAttempts = 12;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" && data.purchase_id) {
          setStatus("paid");
          // Fire Meta Purchase — browser Pixel + server CAPI both use the
          // backend-generated event_id, so Meta deduplicates across all sources.
          if (!purchaseFiredRef.current && data.meta_event_id) {
            purchaseFiredRef.current = true;
            const key = `ao_purchase_fired_${data.meta_event_id}`;
            if (!sessionStorage.getItem(key)) {
              trackAndSend(
                "Purchase",
                {
                  value: (data.amount || 9700) / 100.0,
                  currency: (data.currency || "usd").toUpperCase(),
                  content_name: "AuthorityOS Diagnostic",
                  content_type: "product",
                  content_ids: ["authority_os_diagnostic_97"],
                  order_id: sessionId,
                },
                { eventId: data.meta_event_id, email: data.email },
              );
              sessionStorage.setItem(key, "1");
            }
          }
          // Give beacons/pixel time to flush before route change (fbevents.js
          // may still be loading async on first paint).
          setTimeout(() => navigate(`/assessment/${data.purchase_id}`), 2500);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
        attempts++;
        if (attempts >= maxAttempts) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts++;
        if (attempts >= maxAttempts) setStatus("timeout");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, navigate]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      <div className="eyebrow">Payment</div>
      <h1 className="mt-6 font-serif text-5xl text-[#1A1915] leading-tight">
        {status === "polling" && "Confirming payment…"}
        {status === "paid" && "Confirmed."}
        {status === "failed" && "Payment failed."}
        {status === "timeout" && "Taking longer than usual."}
      </h1>
      <p className="mt-6 text-base text-[#1A1915] leading-relaxed">
        {status === "polling" && "Stripe is verifying your payment. This usually takes under 10 seconds."}
        {status === "paid" && "Routing you to the assessment. If you are not redirected in a moment, please refresh."}
        {status === "failed" && "The transaction did not complete. No charge was made. Please try again."}
        {status === "timeout" && "We could not confirm payment status. If you were charged, an email will follow with access."}
      </p>
    </div>
  );
}
